import React, { useState } from "react";
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import type { Settlement } from "../../../src/pages/loans/LoanDues";
import Modal from "../Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  settlement: Settlement | null;
  discountStr: string;
  additionalStr: string;
  paidOnStr: string;
  setDiscountStr: (s: string) => void;
  setAdditionalStr: (s: string) => void;
  setPaidOnStr: (s: string) => void;
  payableNumeric: number;
  totalDueNumeric: number;
  processingSettlement: boolean;
  onProcess: () => Promise<void>;
  error?: string | null;
  success?: string | null;
  loanId?: number | string | null;
  loan_code?: number | string | null;
};

export default function SettlementModal({
  isOpen,
  onClose,
  settlement,
  discountStr,
  additionalStr,
  paidOnStr,
  setDiscountStr,
  setAdditionalStr,
  setPaidOnStr,
  payableNumeric,
  totalDueNumeric,
  processingSettlement,
  onProcess,
  error,
  success,
  loanId,
  loan_code,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [localProcessing, setLocalProcessing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // When user clicks main button -> open confirm modal
  const handleClickProcess = () => {
    setLocalError(null);
    setConfirmOpen(true);
  };

  // When user confirms -> run the provided onProcess() and close
  const handleConfirm = async () => {
    setLocalError(null);
    try {
      setLocalProcessing(true);
      await onProcess();
      // keep the confirmation modal open while processing and then close it
      setConfirmOpen(false);
    } catch (err: any) {
      // capture errors so user sees them inside the confirm modal
      setLocalError(err?.message ?? String(err ?? "Failed to process"));
    } finally {
      setLocalProcessing(false);
    }
  };

  // Canceling confirmation
  const handleCancelConfirm = () => {
    setLocalError(null);
    setConfirmOpen(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Process Loan Settlement">
        <div className="w-full max-w-5xl mx-auto px-6 py-6 max-h-[84vh] overflow-y-auto">
          {settlement ? (
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="flex-1">
                <div className="mt-6 bg-transparent">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <p className="text-xs text-gray-500">Loan Code</p>
                      <p className="mt-2 text-sm font-semibold text-gray-900 break-words">
                        {settlement.loan_code ?? loan_code ?? "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Total Balance Due</p>
                      <p className="mt-2 text-base font-semibold text-gray-900">
                        {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                          totalDueNumeric
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full md:w-[360px] flex flex-col">
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm">
                  <label className="block text-xs text-gray-600 mb-2">Adjustment Amount</label>
                  <InputField
                    type="number"
                    value={discountStr}
                    onChange={(e: any) => setDiscountStr(e?.target?.value ?? String(e ?? ""))}
                    placeholder="0"
                  />

                  <label className="block text-xs text-gray-600 my-2">Additional Amount</label>
                  <InputField
                    type="number"
                    value={additionalStr}
                    onChange={(e: any) => setAdditionalStr(e?.target?.value ?? String(e ?? ""))}
                    placeholder="0"
                  />

                  <label className="block text-xs text-gray-600 my-2">Paid On</label>
                  <InputField type="date" value={paidOnStr} onChange={(e: any) => setPaidOnStr(e?.target?.value ?? "")} />

                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Payable Amount</p>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-indigo-600">
                        {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(payableNumeric)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ({new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(totalDueNumeric)} -{" "}
                        {discountStr || "0"})
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ({new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(totalDueNumeric)} +{" "}
                        {additionalStr || "0"})
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 md:mt-8 flex justify-between items-center">
                  <div className="w-full md:w-auto">
                    <Button
                      variant="primary"
                      onClick={handleClickProcess}
                      className="w-full md:w-auto"
                      disabled={processingSettlement || localProcessing}
                    >
                      {processingSettlement || localProcessing ? "Processing..." : "Process Settlement"}
                    </Button>
                  </div>
                </div>

                {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}
                {success ? <div className="mt-3 text-sm text-green-600">{success}</div> : null}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">Loading settlement data...</div>
          )}
        </div>
      </Modal>

      {/* Confirmation modal */}
      <Modal isOpen={confirmOpen} onClose={handleCancelConfirm} title="Confirm Settlement">
        <div className="p-4">
          <p className="text-sm text-gray-700">
            You're about to process settlement for{" "}
            <span className="font-semibold">{settlement?.loan_code ?? loan_code ?? settlement?.loan_id ?? loanId ?? "-"}</span>.
          </p>

          <div className="mt-3 text-sm text-gray-600">
            <div>Payable: <span className="font-semibold">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(payableNumeric)}</span></div>
            <div>Total Due: <span className="font-semibold">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(totalDueNumeric)}</span></div>
            <div>Adjustment: <span className="font-semibold">{discountStr || "0"}</span></div>
            <div>Additional: <span className="font-semibold">{additionalStr || "0"}</span></div>
            <div>Paid On: <span className="font-semibold">{paidOnStr || "-"}</span></div>
          </div>

          {localError ? <div className="mt-3 text-sm text-red-600">{localError}</div> : null}

          <div className="mt-5 flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancelConfirm} disabled={localProcessing}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirm} disabled={localProcessing}>
              {localProcessing ? "Processing..." : "Confirm & Process"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
